import { useMemo } from 'react'
import { externalPagesOf, type PropertyHrefFeed } from './property-href'
import { trailingSlashModeOf } from './trailing-slash'

/**
 * The slice of the feed record the property-link resolver reads (#1714 External
 * Property Pages, #1848 trailing-slash preference), with a STABLE identity:
 * `undefined` for every feed that has neither setting, and one memoized object
 * otherwise.
 *
 * Surfaces put this in the dependency list of their link builders. The whole
 * feed would not do: it goes from undefined to an object when the widget record
 * lands, which re-created every builder (and rebuilt the map's pins and open
 * popups) on feeds that never set either feature. This only changes when a
 * setting or the origin does.
 */
export function usePropertyHrefFeed(
  feed: { feed_url?: string | null; additional_info?: { reverseProxy?: unknown } | null } | null | undefined,
): PropertyHrefFeed | undefined {
  const feedUrl = feed?.feed_url
  const externalPages = externalPagesOf(feed)
  // Only the keep state is carried: absent and strip read the same, so a strip
  // feed keeps today's `undefined` identity byte for byte.
  const keepSlash = trailingSlashModeOf(feed) === 'keep'
  return useMemo(
    () =>
      externalPages === undefined && !keepSlash
        ? undefined
        : {
            feed_url: feedUrl,
            additional_info: {
              reverseProxy: {
                ...(externalPages === undefined ? {} : { externalPages }),
                ...(keepSlash ? { trailingSlash: 'keep' as const } : {}),
              },
            },
          },
    [feedUrl, externalPages, keepSlash],
  )
}
